// FRONTEND src/pages/Admin.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 23.04.2025 to improve responsiveness, accessibility, and loading animation
//

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';
import Dashboard from '../components/admin/Dashboard';
import TeamEditor from '../components/admin/TeamEditor';
import CarsEditor from '../components/admin/CarsEditor';
import CarPhotosEditor from '../components/admin/CarPhotosEditor';
import EventsEditor from '../components/admin/EventsEditor';
import GalleryEditor from '../components/admin/GalleryEditor';
import SponsorsEditor from '../components/admin/SponsorsEditor';
import ProductsEditor from '../components/admin/ProductsEditor';
import AchievementsEditor from '../components/admin/AchievementsEditor';
import AchievementStatsEditor from '../components/admin/AchievementStatsEditor';
import SettingsEditor from '../components/admin/SettingsEditor';
import MessagesViewer from '../components/admin/MessagesViewer';
import OrdersEditor from '../components/admin/OrdersEditor';
import BlogEditor from '../components/admin/BlogEditor';

const Admin = () => {
  const { currentUser, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const language = getUserLanguage();

  useEffect(() => {
    document.title = language === 'cs'
      ? 'Administrace | NO RISK NO DRIFT'
      : 'Administration | NO RISK NO DRIFT';
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, language]);

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isActive = (path) => {
    // Zlepšená detekce aktivní trasy (podpora dynamických cest)
    if (path === '') return location.pathname === '/admin';
    return location.pathname.startsWith(`/admin${path}`);
  };

  if (loading) {
    return (
      <div className="admin-interface min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="admin-interface min-h-screen bg-black flex">
      <div
        className={`admin-sidebar fixed inset-y-0 left-0 w-64 bg-dark-gray transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 transition-transform duration-300 ease-in-out z-50`}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-gray-800">
            <Link
              to="/"
              className="flex items-center justify-center"
              onClick={closeSidebar}
              aria-label={language === 'cs' ? 'Přejít na domovskou stránku' : 'Go to homepage'}
            >
              <img
                src={`${IMAGE_BASE_URL}/images/logo-A2.png`}
                alt="NO RISK NO DRIFT Logo"
                className="h-10 w-auto"
              />
            </Link>
          </div>
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">
                {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="ml-3">
                <p className="text-white font-medium">{currentUser?.username || 'Admin'}</p>
                <p className="text-gray-500 text-xs">{currentUser?.role || 'admin'}</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            <ul className="space-y-1">
              <li>
                <Link
                  to="/admin"
                  className={`admin-nav-link ${isActive('') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Dashboard' : 'Go to Dashboard'}
                >
                  <i className="fas fa-tachometer-alt admin-nav-icon"></i>
                  {language === 'cs' ? 'Dashboard' : 'Dashboard'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/orders"
                  className={`admin-nav-link ${isActive('/orders') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Objednávky' : 'Go to Orders'}
                >
                  <i className="fas fa-box admin-nav-icon"></i>
                  {language === 'cs' ? 'Objednávky' : 'Orders'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/blog"
                  className={`admin-nav-link ${isActive('/blog') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Blog' : 'Go to Blog'}
                >
                  <i className="fas fa-blog admin-nav-icon"></i>
                  {language === 'cs' ? 'Blog' : 'Blog'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/team"
                  className={`admin-nav-link ${isActive('/team') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Tým' : 'Go to Team'}
                >
                  <i className="fas fa-users admin-nav-icon"></i>
                  {language === 'cs' ? 'Tým' : 'Team'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/cars"
                  className={`admin-nav-link ${isActive('/cars') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Auta' : 'Go to Cars'}
                >
                  <i className="fas fa-car admin-nav-icon"></i>
                  {language === 'cs' ? 'Auta' : 'Cars'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/events"
                  className={`admin-nav-link ${isActive('/events') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Události' : 'Go to Events'}
                >
                  <i className="fas fa-calendar-alt admin-nav-icon"></i>
                  {language === 'cs' ? 'Události' : 'Events'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/gallery"
                  className={`admin-nav-link ${isActive('/gallery') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Galerii' : 'Go to Gallery'}
                >
                  <i className="fas fa-images admin-nav-icon"></i>
                  {language === 'cs' ? 'Galerie' : 'Gallery'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/sponsors"
                  className={`admin-nav-link ${isActive('/sponsors') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Sponzory' : 'Go to Sponsors'}
                >
                  <i className="fas fa-handshake admin-nav-icon"></i>
                  {language === 'cs' ? 'Sponzoři' : 'Sponsors'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/products"
                  className={`admin-nav-link ${isActive('/products') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Produkty' : 'Go to Products'}
                >
                  <i className="fas fa-shopping-cart admin-nav-icon"></i>
                  {language === 'cs' ? 'Produkty' : 'Products'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/achievements"
                  className={`admin-nav-link ${isActive('/achievements') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Úspěchy' : 'Go to Achievements'}
                >
                  <i className="fas fa-trophy admin-nav-icon"></i>
                  {language === 'cs' ? 'Úspěchy' : 'Achievements'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/achievement-stats"
                  className={`admin-nav-link ${isActive('/achievement-stats') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Statistiky úspěchů' : 'Go to Achievement Stats'}
                >
                  <i className="fas fa-chart-bar admin-nav-icon"></i>
                  {language === 'cs' ? 'Statistiky úspěchů' : 'Achievement Stats'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/messages"
                  className={`admin-nav-link ${isActive('/messages') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Zprávy' : 'Go to Messages'}
                >
                  <i className="fas fa-envelope admin-nav-icon"></i>
                  {language === 'cs' ? 'Zprávy' : 'Messages'}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/settings"
                  className={`admin-nav-link ${isActive('/settings') ? 'admin-nav-link-active' : ''}`}
                  onClick={closeSidebar}
                  aria-label={language === 'cs' ? 'Přejít na Nastavení' : 'Go to Settings'}
                >
                  <i className="fas fa-cog admin-nav-icon"></i>
                  {language === 'cs' ? 'Nastavení' : 'Settings'}
                </Link>
              </li>
            </ul>
          </nav>
          <div className="p-4 border-t border-gray-800">
            <button
              onClick={handleLogout}
              className="admin-nav-link w-full"
              aria-label={language === 'cs' ? 'Odhlásit se' : 'Log Out'}
            >
              <i className="fas fa-sign-out-alt admin-nav-icon"></i>
              {language === 'cs' ? 'Odhlásit se' : 'Log Out'}
            </button>
          </div>
        </div>
      </div>
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : ''} md:ml-64 transition-all duration-300`}>
        <header className="bg-dark-gray shadow-md">
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleSidebar}
                className="md:hidden p-2 text-gray-400 hover:text-white focus:outline-none"
                aria-label={sidebarOpen ? 'Zavřít postranní panel' : 'Otevřít postranní panel'}
              >
                <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'} w-6 h-6`}></i>
              </button>
              <div className="relative">
                <button
                  className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white"
                  aria-label={language === 'cs' ? 'Profil uživatele' : 'User profile'}
                >
                  {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
                </button>
              </div>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white"
                aria-label={language === 'cs' ? 'Zobrazit web v novém okně' : 'View website in new window'}
              >
                <i className="fas fa-external-link-alt"></i>
                <span className="ml-1 text-sm">
                  {language === 'cs' ? 'Zobrazit web' : 'View Website'}
                </span>
              </a>
            </div>
          </div>
        </header>
        <main className="px-4 py-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/orders" element={<OrdersEditor />} />
            <Route path="/team" element={<TeamEditor />} />
            <Route path="/blog" element={<BlogEditor />} />
            <Route path="/cars" element={<CarsEditor />} />
            <Route path="/car-photos/:slug" element={<CarPhotosEditor />} />
            <Route path="/events" element={<EventsEditor />} />
            <Route path="/gallery" element={<GalleryEditor />} />
            <Route path="/sponsors" element={<SponsorsEditor />} />
            <Route path="/products" element={<ProductsEditor />} />
            <Route path="/achievements" element={<AchievementsEditor />} />
            <Route path="/achievement-stats" element={<AchievementStatsEditor />} />
            <Route path="/messages" element={<MessagesViewer />} />
            <Route path="/settings" element={<SettingsEditor />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Admin;